import axiosClient from './axiosClient';

export type EntityType = 'OPPORTUNITY' | 'SECTION' | 'DOCUMENT' | 'REQUIREMENT';

export interface Thread {
  id: string;
  entityType: EntityType;
  entityId: string;
  subject: string;
  isResolved: boolean;
  resolvedByName?: string;
  resolvedAt?: string;
  commentCount: number;
  lastCommentAt: string;
  createdAt: string;
  createdBy: string;
}

export interface MentionDto {
  userId: string;
  fullName: string;
}

export interface Comment {
  id: string;
  threadId: string;
  parentId?: string;
  authorId: string;
  authorName: string;
  authorInitials: string;
  body: string;
  mentions: MentionDto[];
  attachmentUrl?: string;
  attachmentName?: string;
  isEdited: boolean;
  createdAt: string;
  replies: Comment[];
}

export interface CreateThreadRequest {
  entityType?: EntityType;
  entityId?: string;
  subject?: string;
  body?: string;
  mentionedUserIds?: string[];
}

export interface CreateCommentRequest {
  body: string;
  parentId?: string;
  mentionedUserIds?: string[];
  attachmentUrl?: string;
  attachmentName?: string;
}

export interface MentionUser {
  id: string;
  fullName: string;
  role?: string;
}

export interface InboxItem {
  threadId: string;
  threadSubject: string;
  entityType: EntityType;
  entityId: string;
  threadResolved: boolean;
  commentId: string;
  commentBody: string;
  commentCreatedAt: string;
  authorId: string;
  authorName: string;
  authorInitials: string;
  mentionedUserId: string;
  mentionedUserName: string;
}

export const discussionApi = {
  getAllThreads: async (): Promise<Thread[]> => {
    const response = await axiosClient.get('/discussions/threads');
    return response.data;
  },

  getThreads: async (entityType: EntityType, entityId: string): Promise<Thread[]> => {
    const response = await axiosClient.get('/discussions/threads', {
      params: { entityType, entityId },
    });
    return response.data;
  },

  getThread: async (threadId: string): Promise<Thread> => {
    const response = await axiosClient.get(`/discussions/threads/${threadId}`);
    return response.data;
  },

  getComments: async (threadId: string): Promise<Comment[]> => {
    const response = await axiosClient.get(`/discussions/threads/${threadId}/comments`);
    return response.data;
  },

  createThread: async (data: CreateThreadRequest): Promise<Thread> => {
    const response = await axiosClient.post('/discussions/threads', data);
    return response.data;
  },

  addComment: async (threadId: string, data: CreateCommentRequest): Promise<Comment> => {
    const response = await axiosClient.post(`/discussions/threads/${threadId}/comments`, data);
    return response.data;
  },

  resolveThread: async (threadId: string): Promise<void> => {
    await axiosClient.put(`/discussions/threads/${threadId}/resolve`);
  },

  reopenThread: async (threadId: string): Promise<void> => {
    await axiosClient.put(`/discussions/threads/${threadId}/reopen`);
  },

  deleteThread: async (threadId: string): Promise<void> => {
    await axiosClient.delete(`/discussions/threads/${threadId}`);
  },

  editComment: async (commentId: string, body: string): Promise<Comment> => {
    const response = await axiosClient.put(`/discussions/comments/${commentId}`, { body });
    return response.data;
  },

  deleteComment: async (commentId: string): Promise<void> => {
    await axiosClient.delete(`/discussions/comments/${commentId}`);
  },

  getUsers: async (): Promise<MentionUser[]> => {
    const response = await axiosClient.get('/discussions/mentions/users');
    return response.data;
  },

  getTaggedMe: async (): Promise<InboxItem[]> => {
    const response = await axiosClient.get('/discussions/mentions/tagged-me');
    return response.data;
  },

  getITagged: async (): Promise<InboxItem[]> => {
    const response = await axiosClient.get('/discussions/mentions/i-tagged');
    return response.data;
  },
};
